import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  CircularProgress,
  TextField,
  MenuItem,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Stack,
  Tab,
  Tabs,
  useTheme
} from '@mui/material';
import {
  DescriptionOutlined as DocIcon,
  Visibility as ViewIcon,
  FileDownloadOutlined as DownloadIcon,
  EmailOutlined as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import useOrganizationSearch from '../hooks/useOrganizationSearch';
import { Combobox } from '../components/ui/combobox';
import { getBrandedTableHeadSx } from '../utils/colorUtils';
import { ProTableStats } from '../components/pro-table';

// Tile definitions — mirror the Thaklis expiring-documents report buckets.
const TILES = [
  { key: 'TOTAL', label: 'Total Expiring', field: 'totalExpiring', color: '#1976d2' },
  { key: 'R0_30', label: '0 - 30 days', field: 'expiring0_30', color: '#2e7d32' },
  { key: 'R31_60', label: '31 - 60 days', field: 'expiring31_60', color: '#ed6c02' },
  { key: 'R61_90', label: '61 - 90 days', field: 'expiring61_90', color: '#9c27b0' },
  { key: 'EXPIRED', label: 'Already Expired', field: 'alreadyExpired', color: '#d32f2f' }
];

const toISO = (d) => {
  const dd = new Date(d);
  dd.setHours(0, 0, 0, 0);
  return dd.toISOString().split('T')[0];
};

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const addDays = (base, n) => {
  const d = new Date(base);
  d.setDate(d.getDate() + n);
  return d;
};

// Compute the {fromDate, toDate} expiry window for a selected tile (null tile = all with expiry).
const rangeForTile = (key) => {
  const today = startOfToday();
  switch (key) {
    case 'TOTAL':   return { fromDate: toISO(today), toDate: null };
    case 'R0_30':   return { fromDate: toISO(today), toDate: toISO(addDays(today, 30)) };
    case 'R31_60':  return { fromDate: toISO(addDays(today, 31)), toDate: toISO(addDays(today, 60)) };
    case 'R61_90':  return { fromDate: toISO(addDays(today, 61)), toDate: toISO(addDays(today, 90)) };
    case 'EXPIRED': return { fromDate: null, toDate: toISO(addDays(today, -1)) };
    default:        return { fromDate: null, toDate: null };
  }
};

const prettyType = (t) => {
  if (!t) return '';
  return String(t).replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysUntil = (expiry) => {
  if (!expiry) return null;
  const e = new Date(expiry);
  if (isNaN(e.getTime())) return null;
  e.setHours(0, 0, 0, 0);
  return Math.round((e - startOfToday()) / (1000 * 60 * 60 * 24));
};

const ORG_FILTER_SENTINELS = [{ id: '', name: 'All Clients' }, { id: 'WALKIN', name: 'Walk-in (no client)' }];

const DocumentExpiry = ({ currentUser }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [kind, setKind] = useState(() => (
    new URLSearchParams(window.location.search).get('kind') === 'COMPANY' ? 'COMPANY' : 'EMPLOYEE'));
  const isCompany = kind === 'COMPANY';
  const isDark = theme.palette.mode === 'dark';

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeTile, setActiveTile] = useState(null);

  const typingCenterId = currentUser?.typingCenterId || currentUser?.typing_center_id;

  // Internal-org (TC client) filter: '' = all, 'WALKIN' = walk-ins only, else an internal org id.
  const {
    options: internalOrgs,
    loading: internalOrgsLoading,
    onInputChange: onInternalOrgInputChange
  } = useOrganizationSearch({
    typingCenterId,
    mapOption: (o) => ({
      id: o.id,
      name: o.name || o.organization_name || o.code || o.id,
      email: o.email || o.primary_email || o.primaryEmail || ''
    })
  });
  const [orgFilter, setOrgFilter] = useState('');
  // Held separately: the selected client may fall outside the current search results.
  const [selectedOrgOption, setSelectedOrgOption] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalElements, setTotalElements] = useState(0);

  // Row shown in the details popup
  const [detailRow, setDetailRow] = useState(null);

  const range = useMemo(() => rangeForTile(activeTile), [activeTile]);

  // Translate the org filter selection into query params shared by list + summary.
  const orgFilterParams = useCallback(() => {
    const base = isCompany ? { kind: 'COMPANY' } : {};
    if (orgFilter === 'WALKIN') return isCompany ? base : { walkinOnly: true };
    if (orgFilter) return { ...base, internalOrgId: orgFilter };
    return base;
  }, [orgFilter, isCompany]);

  // Map internal org id → name for the Client column.
  const orgNameById = useMemo(() => {
    const m = {};
    internalOrgs.forEach((o) => { if (o.id) m[o.id] = o.name; });
    return m;
  }, [internalOrgs]);

  // Human-readable labels for the current view, sent to the export/email endpoints so the
  // CSV/email state what's being shown.
  const scopeLabel = useMemo(() => {
    if (orgFilter === 'WALKIN') return 'Walk-in (no client)';
    if (orgFilter) return selectedOrgOption?.name || orgNameById[orgFilter] || 'Client';
    return 'All Clients';
  }, [orgFilter, orgNameById, selectedOrgOption]);

  const rangeLabel = useMemo(() => {
    const t = TILES.find((x) => x.key === activeTile);
    if (!t) return '';
    if (activeTile === 'TOTAL') return 'Total expiring';
    if (activeTile === 'EXPIRED') return 'Already expired';
    return `Expiring in ${t.label}`;
  }, [activeTile]);

  // Params describing exactly what's on screen (org filter + tile range + labels).
  const currentViewParams = useCallback(() => {
    const p = { ...orgFilterParams(), scopeLabel, rangeLabel };
    if (range.fromDate) p.fromDate = range.fromDate;
    if (range.toDate) p.toDate = range.toDate;
    return p;
  }, [orgFilterParams, scopeLabel, rangeLabel, range]);

  // Export / email state
  const [exporting, setExporting] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientInput, setRecipientInput] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [snack, setSnack] = useState('');

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await ApiService.exportDocumentExpiry(currentViewParams());
      const blobUrl = window.URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `document-expiry-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      console.error('Export failed', err);
      setSnack('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const openEmailDialog = () => {
    const org = internalOrgs.find((o) => o.id === orgFilter);
    setRecipients(org?.email ? [org.email] : []);
    setRecipientInput('');
    setEmailSubject(`Document Expiry Report — ${scopeLabel}`);
    setEmailMessage('Please find the document expiry report attached.');
    setEmailError(null);
    setEmailOpen(true);
  };

  const addRecipient = (value) => {
    const v = String(value || '').trim().replace(/,+$/, '').trim();
    if (v && !recipients.includes(v)) setRecipients((prev) => [...prev, v]);
    setRecipientInput('');
  };

  const removeRecipient = (email) => setRecipients((prev) => prev.filter((e) => e !== email));

  const handleSendEmail = async () => {
    const pending = recipientInput.trim().replace(/,+$/, '').trim();
    const all = pending && !recipients.includes(pending) ? [...recipients, pending] : recipients;
    if (all.length === 0) {
      setEmailError('Add at least one recipient');
      return;
    }
    setEmailSending(true);
    setEmailError(null);
    try {
      await ApiService.emailDocumentExpiry({
        recipients: all,
        subject: emailSubject,
        message: emailMessage,
        ...currentViewParams()
      });
      setEmailOpen(false);
      setSnack('Report emailed successfully');
    } catch (err) {
      setEmailError(err.response?.data?.error || err.response?.data?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };


  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await ApiService.get('/api/document-expiry/summary', { params: orgFilterParams() });
      setSummary(res.data?.data || null);
    } catch (err) {
      console.error('Error loading document-expiry summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [orgFilterParams]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: rowsPerPage, ...orgFilterParams() };
      if (range.fromDate) params.fromDate = range.fromDate;
      if (range.toDate) params.toDate = range.toDate;
      const res = await ApiService.get('/api/document-expiry', { params });
      const data = res.data?.data || {};
      setRows(Array.isArray(data.content) ? data.content : []);
      setTotalElements(data.totalElements ?? data.total_elements ?? 0);
    } catch (err) {
      console.error('Error loading document-expiry report:', err);
      setRows([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, range, orgFilterParams]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleKindChange = (e, next) => {
    setKind(next);
    setActiveTile(null);
    setPage(0);
    if (orgFilter === 'WALKIN') {
      setOrgFilter('');
      setSelectedOrgOption(null);
    }
  };

  const handleTileClick = (key) => {
    setActiveTile((prev) => (prev === key ? null : key));
    setPage(0);
  };

  const daysChip = (expiry) => {
    const d = daysUntil(expiry);
    if (d === null) return <Chip label="—" size="small" variant="outlined" />;
    if (d < 0) {
      return <Chip label={`Expired ${Math.abs(d)}d ago`} size="small"
        sx={{ bgcolor: isDark ? 'rgba(211,47,47,0.18)' : '#fdecea', color: '#d32f2f', fontWeight: 600 }} />;
    }
    if (d <= 30) {
      return <Chip label={`${d}d left`} size="small"
        sx={{ bgcolor: isDark ? 'rgba(237,108,2,0.18)' : '#fff4e5', color: '#ed6c02', fontWeight: 600 }} />;
    }
    if (d <= 90) {
      return <Chip label={`${d}d left`} size="small"
        sx={{ bgcolor: isDark ? 'rgba(2,136,209,0.18)' : '#e5f3fb', color: '#0288d1', fontWeight: 600 }} />;
    }
    return <Chip label={`${d}d left`} size="small"
      sx={{ bgcolor: isDark ? 'rgba(46,125,50,0.18)' : '#edf7ed', color: '#2e7d32', fontWeight: 600 }} />;
  };

  const columns = isCompany
    ? ['Client', 'Document Type', 'Doc Number', 'Expiry Date', 'Status', 'Actions']
    : ['Employee', 'Employee ID', 'Client', 'Document Type', 'Doc Number', 'Issue Date', 'Expiry Date', 'Status', 'Actions'];

  const isWalkinRow = (r) => String(r.sourceInternalOrgId || '').toLowerCase() === 'walkin';

  // clientName is resolved by the backend for every row; the id is only a last resort.
  const clientLabel = (r) => {
    const sid = r.sourceInternalOrgId;
    if (!sid) return '—';
    return r.clientName || orgNameById[sid] || sid;
  };

  return (
    <Box>
      <Tabs value={kind} onChange={handleKindChange} sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="EMPLOYEE" label="Employee documents" />
        <Tab value="COMPANY" label="Company documents" />
      </Tabs>
      <ProTableStats
        loading={summaryLoading}
        cards={TILES.map((tile) => ({
          label: tile.label,
          value: summary ? (summary[tile.field] ?? 0) : 0,
          color: tile.color,
          highlight: activeTile === tile.key,
          onClick: () => handleTileClick(tile.key),
        }))}
      />

      {/* Filter Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Combobox
            className="w-full [&>button]:h-8 [&>button]:text-[0.8125rem]"
            options={(isCompany ? ORG_FILTER_SENTINELS.slice(0, 1) : ORG_FILTER_SENTINELS).concat(internalOrgs)}
            value={selectedOrgOption || ORG_FILTER_SENTINELS[0]}
            onChange={(org) => {
              const next = org || ORG_FILTER_SENTINELS[0];
              setSelectedOrgOption(next);
              setOrgFilter(next.id);
              setPage(0);
            }}
            onSearchChange={(text) => onInternalOrgInputChange(null, text, 'input')}
            getOptionLabel={(o) => o?.name || ''}
            getOptionKey={(o) => o?.id || 'all'}
            loading={internalOrgsLoading}
            placeholder="All Clients"
            searchPlaceholder="Search by name or TRN..."
            emptyText="No clients found"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
            sx={{ color: 'secondary.main', borderColor: 'secondary.main' }}
          >
            Export CSV
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<EmailIcon />}
            onClick={openEmailDialog}
            sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
          >
            Email Report
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'secondary.main' }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <DocIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 1 }} />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {activeTile ? 'No documents in this range' : 'No documents with an expiry date'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', mt: 0.5 }}>
              {isCompany
                ? 'Company documents with an expiry date appear here as they near expiry.'
                : 'Documents tracked from work orders will appear here as they near expiry.'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead sx={getBrandedTableHeadSx(theme)}>
                  <TableRow>
                    {columns.map((c) => (
                      <TableCell
                        key={c}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {c}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isCompany && rows.map((r) => (
                    <TableRow key={r.document_group_id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.client_name || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.document_type_label || prettyType(r.document_type)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.document_number || '—'}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.expiry_date)}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{daysChip(r.expiry_date)}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" title="Open the client's documents"
                          onClick={() => navigate(`/organizations/internal/${r.internal_org_id}?tab=documents`)}>
                          <ViewIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!isCompany && rows.map((r) => {
                    const name = `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—';
                    return (
                      <TableRow key={r.documentId} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{name}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.employeeId || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {isWalkinRow(r)
                            ? <Chip label="Walk-in" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
                            : clientLabel(r)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{prettyType(r.documentType)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.documentNumber || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.issueDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(r.expiryDate)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{daysChip(r.expiryDate)}</TableCell>
                        <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton size="small" onClick={() => setDetailRow(r)} title="View details">
                            <ViewIcon fontSize="small" sx={{ color: 'secondary.main' }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalElements}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </>
        )}
      </Paper>

      {/* Document details */}
      <Dialog open={Boolean(detailRow)} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'secondary.main', fontWeight: 600 }}>Document Details</DialogTitle>
        <DialogContent dividers>
          {detailRow && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <DetailRow label="Employee" value={`${detailRow.firstName || ''} ${detailRow.lastName || ''}`.trim() || '—'} />
              <DetailRow label="Employee ID" value={detailRow.employeeId || '—'} />
              <DetailRow
                label="Client"
                value={isWalkinRow(detailRow) ? 'Walk-in' : clientLabel(detailRow)}
              />
              <Divider />
              <DetailRow label="Phone" value={detailRow.mobileNumber || '—'} />
              <DetailRow label="Email" value={detailRow.email || '—'} />
              <Divider />
              <DetailRow label="Document Type" value={prettyType(detailRow.documentType) || '—'} />
              <DetailRow label="Document Number" value={detailRow.documentNumber || '—'} />
              <DetailRow label="File Name" value={detailRow.fileName || '—'} />
              <DetailRow label="Issue Date" value={formatDate(detailRow.issueDate)} />
              <DetailRow label="Expiry Date" value={formatDate(detailRow.expiryDate)} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', minWidth: 130 }}>Status</Typography>
                {daysChip(detailRow.expiryDate)}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Email report */}
      <Dialog open={emailOpen} onClose={() => setEmailOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'secondary.main', fontWeight: 600 }}>Email Document Expiry Report</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Sends the report as currently filtered — <b>{scopeLabel}</b>
              {rangeLabel ? <> · <b>{rangeLabel}</b></> : ''} — from your typing center's configured email account.
            </Typography>

            {emailError && <Alert severity="error" onClose={() => setEmailError(null)}>{emailError}</Alert>}

            <Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: recipients.length ? 1 : 0 }}>
                {recipients.map((email) => (
                  <Chip key={email} label={email} onDelete={() => removeRecipient(email)} size="small" />
                ))}
              </Box>
              <TextField
                fullWidth
                size="small"
                label="Recipients"
                placeholder="Type an email and press Enter"
                value={recipientInput}
                onChange={(e) => setRecipientInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addRecipient(recipientInput); }
                }}
                onBlur={() => { if (recipientInput.trim()) addRecipient(recipientInput); }}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label="Message"
              value={emailMessage}
              onChange={(e) => setEmailMessage(e.target.value)}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailOpen(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSendEmail}
            disabled={emailSending}
            startIcon={emailSending ? <CircularProgress size={16} color="inherit" /> : <EmailIcon />}
            sx={{ bgcolor: 'secondary.main', '&:hover': { bgcolor: 'secondary.dark' } }}
          >
            {emailSending ? 'Sending…' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.includes('fail') || snack.includes('Fail') ? 'error' : 'success'}
          onClose={() => setSnack('')}
          sx={{ width: '100%' }}
        >
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', minWidth: 130 }}>{label}</Typography>
    <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, wordBreak: 'break-word' }}>{value}</Typography>
  </Box>
);

export default DocumentExpiry;
